// src/pages/AddEditCoupon.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, TextField, Button, Stack, Grid, MenuItem,
  Select, FormControl, InputLabel, Switch, Chip,
  Alert, Snackbar, CircularProgress, Divider, InputAdornment, alpha,
  Autocomplete, Avatar, Card, CardContent, useTheme, IconButton, Tooltip
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PublicIcon from '@mui/icons-material/Public';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import StoreIcon from '@mui/icons-material/Store';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PercentIcon from '@mui/icons-material/Percent';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EventIcon from '@mui/icons-material/Event';
import LockIcon from '@mui/icons-material/Lock';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { createCoupon, updateCoupon, fetchCouponById } from '../couponApi';
import { fetchBranches } from '../branchesApi';
import { fetchUsers } from '../usersApi';

const CURRENCIES = [
  { value: 'YER', label: 'YER - ريال يمني قديم', symbol: 'ر.ي' },
  { value: 'YRN', label: 'YRN - ريال يمني جديد', symbol: 'ر.ي.ج' },
  { value: 'USD', label: 'USD - دولار أمريكي', symbol: '$' },
  { value: 'SAR', label: 'SAR - ريال سعودي', symbol: 'ر.س' },
];

// ✅ FIX: Moved OUTSIDE the component to prevent re-creation on every render
function SectionCard({ icon, title, subtitle, color, children }) {
  return (
    <Card
      elevation={0}
      sx={{
        mb: 3,
        borderRadius: 3,
        border: (theme) => `1px solid ${alpha(color || theme.palette.primary.main, 0.2)}`,
        overflow: 'visible'
      }}
    >
      <Box
        sx={{
          px: 3, py: 2,
          bgcolor: (theme) => alpha(color || theme.palette.primary.main, 0.04),
          borderBottom: (theme) => `1px solid ${alpha(color || theme.palette.primary.main, 0.1)}`
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar
            sx={{
              bgcolor: (theme) => alpha(color || theme.palette.primary.main, 0.15),
              color: color || 'primary.main',
              width: 36,
              height: 36
            }}
          >
            {icon}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
            {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
          </Box>
        </Stack>
      </Box>
      <CardContent sx={{ px: 3, py: 2.5 }}>
        {children}
      </CardContent>
    </Card>
  );
}

// ✅ FIX: Helper functions outside component
const getUserDisplayName = (u) => u.FullName || u.Name || '';
const getUserPhone = (u) => u.Phone || u.PhoneNumber || '';
const getUserInitial = (u) => ((getUserDisplayName(u) || '?').charAt(0)).toUpperCase();
const getUserLabel = (u) => {
  const parts = [getUserDisplayName(u), getUserPhone(u), u.Email || ''].filter(Boolean);
  return parts.length > 0 ? parts.join(' — ') : `User #${u.Id}`;
};
const getUserChipLabel = (u) => getUserDisplayName(u) || getUserPhone(u) || `#${u.Id}`;
const getUserSecondary = (u) => [getUserPhone(u), u.Email].filter(Boolean).join(' • ') || `ID: ${u.Id}`;

export default function AddEditCoupon() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [codeCopied, setCodeCopied] = useState(false);

  const [form, setForm] = useState({
    code: '',
    type: 'discount',
    discountType: 'percentage',
    value: '',
    currency: 'YER',
    minOrderAmount: '',
    maxDiscountAmount: '',
    usageLimit: '',
    usageLimitPerUser: '1',
    startDate: '',
    endDate: '',
    isActive: true,
    description: '',
    branchIds: [],
    userIds: []
  });

  const [errors, setErrors] = useState({});

  // ✅ FIX: Memoize derived values
  const selectedBranches = useMemo(
    () => branches.filter(b => form.branchIds.includes(b.Id)),
    [branches, form.branchIds]
  );
  const selectedUsers = useMemo(
    () => users.filter(u => form.userIds.includes(u.Id)),
    [users, form.userIds]
  );

  useEffect(() => {
    loadBranches();
    loadUsers();
    if (isEdit) loadCoupon();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadBranches = async () => {
    try {
      setBranchesLoading(true);
      const data = await fetchBranches();
      setBranches(data || []);
    } catch (err) {
      console.error('Error loading branches:', err);
    } finally {
      setBranchesLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const data = await fetchUsers();
      setUsers(data || []);
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadCoupon = async () => {
    try {
      setLoading(true);
      const data = await fetchCouponById(id);
      setForm({
        code: data.Code || '',
        type: data.Type || 'discount',
        discountType: data.DiscountType || 'percentage',
        value: data.Value?.toString() || '',
        currency: data.Currency || 'YER',
        minOrderAmount: data.MinOrderAmount?.toString() || '',
        maxDiscountAmount: data.MaxDiscountAmount?.toString() || '',
        usageLimit: data.UsageLimit?.toString() || '',
        usageLimitPerUser: data.UsageLimitPerUser?.toString() || '1',
        startDate: data.StartDate ? new Date(data.StartDate).toISOString().slice(0, 16) : '',
        endDate: data.EndDate ? new Date(data.EndDate).toISOString().slice(0, 16) : '',
        isActive: data.IsActive !== false,
        description: data.Description || '',
        branchIds: data.BranchIds || [],
        userIds: data.UserIds || []
      });
    } catch (err) {
      setSnackbar({ open: true, message: t('ErrorLoadingCoupon') || 'Error loading coupon', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // ✅ FIX: Stabilize with useCallback
  const updateField = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleChange = useCallback((field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(prev => ({ ...prev, [field]: val }));
    setErrors(prev => {
      if (prev[field]) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return prev;
    });
  }, []);

  const generateCode = useCallback(() => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
    setForm(prev => ({ ...prev, code }));
  }, []);

  const copyCode = useCallback(() => {
    if (form.code) {
      navigator.clipboard.writeText(form.code.toUpperCase());
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  }, [form.code]);

  const validate = () => {
    const newErrors = {};
    if (!form.code.trim()) newErrors.code = t('Required') || 'Required';
    if (!form.value || parseFloat(form.value) <= 0) newErrors.value = t('MustBePositive') || 'Must be a positive number';
    if (form.discountType === 'percentage' && parseFloat(form.value) > 100) newErrors.value = t('MaxPercentage') || 'Cannot exceed 100%';
    if (form.startDate && form.endDate && new Date(form.startDate) >= new Date(form.endDate)) {
      newErrors.endDate = t('EndAfterStart') || 'End date must be after start date';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setSaving(true);
      const payload = {
        code: form.code.trim().toUpperCase(),
        type: form.type,
        discountType: form.discountType,
        value: parseFloat(form.value),
        currency: form.currency,
        minOrderAmount: form.minOrderAmount ? parseFloat(form.minOrderAmount) : 0,
        maxDiscountAmount: form.maxDiscountAmount ? parseFloat(form.maxDiscountAmount) : null,
        usageLimit: form.usageLimit ? parseInt(form.usageLimit) : null,
        usageLimitPerUser: form.usageLimitPerUser ? parseInt(form.usageLimitPerUser) : 1,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        isActive: form.isActive,
        description: form.description || null,
        branchIds: form.branchIds,
        userIds: form.userIds
      };

      if (isEdit) {
        await updateCoupon(id, payload);
        setSnackbar({ open: true, message: t('CouponUpdated') || 'Coupon updated successfully', severity: 'success' });
      } else {
        await createCoupon(payload);
        setSnackbar({ open: true, message: t('CouponCreated') || 'Coupon created successfully', severity: 'success' });
      }

      setTimeout(() => navigate('/admin/coupons'), 1000);
    } catch (err) {
      const msg = err.response?.data?.message || (t('ErrorSavingCoupon') || 'Error saving coupon');
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ✅ FIX: Stable callbacks for Autocomplete onChange
  const handleBranchesChange = useCallback((_, newValue) => {
    setForm(prev => ({ ...prev, branchIds: newValue.map(b => b.Id) }));
  }, []);

  const handleUsersChange = useCallback((_, newValue) => {
    setForm(prev => ({ ...prev, userIds: newValue.map(u => u.Id) }));
  }, []);

  const clearBranches = useCallback(() => {
    setForm(prev => ({ ...prev, branchIds: [] }));
  }, []);

  const clearUsers = useCallback(() => {
    setForm(prev => ({ ...prev, userIds: [] }));
  }, []);

  const toggleActive = useCallback((e) => {
    setForm(prev => ({ ...prev, isActive: e.target.checked }));
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={15}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={40} />
          <Typography color="text.secondary">{t('LoadingCoupon') || 'Loading coupon...'}</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/coupons')}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            {t('Back') || 'Back'}
          </Button>
          <Divider orientation="vertical" flexItem />
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar sx={{ bgcolor: 'primary.main', width: 38, height: 38 }}>
              <LocalOfferIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
                {isEdit ? (t('EditCoupon') || 'Edit Coupon') : (t('AddCoupon') || 'Add New Coupon')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isEdit ? (t('EditCouponDesc') || 'Modify coupon settings') : (t('AddCouponDesc') || 'Create a new discount coupon or purchase voucher')}
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={form.isActive ? (t('Active') || 'Active') : (t('Inactive') || 'Inactive')}
            color={form.isActive ? 'success' : 'default'}
            size="small"
            variant={form.isActive ? 'filled' : 'outlined'}
          />
          <Switch checked={form.isActive} onChange={toggleActive} color="success" />
        </Stack>
      </Stack>

      <form onSubmit={handleSubmit}>

        {/* SECTION 1: BASIC INFO */}
        <SectionCard
          icon={<LocalOfferIcon fontSize="small" />}
          title={t('BasicInfo') || 'Basic Information'}
          subtitle={t('BasicInfoDesc') || 'Coupon code, type and description'}
          color="#1976d2"
        >
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth
                label={t('CouponCode') || 'Coupon Code'}
                value={form.code}
                onChange={handleChange('code')}
                error={!!errors.code}
                helperText={errors.code || (t('CouponCodeHelp') || 'Unique code customers will enter at checkout')}
                placeholder="e.g. SUMMER25"
                InputProps={{
                  sx: { fontFamily: 'monospace', fontWeight: 700, fontSize: '1.1rem', textTransform: 'uppercase', letterSpacing: 1 },
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocalOfferIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Stack direction="row" spacing={0.5}>
                        {form.code && (
                          <Tooltip title={codeCopied ? (t('Copied') || 'Copied!') : (t('CopyCode') || 'Copy')}>
                            <IconButton size="small" onClick={copyCode}>
                              {codeCopied ? <CheckCircleIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                        <Button size="small" onClick={generateCode} startIcon={<AutorenewIcon />} variant="outlined" sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.75rem' }}>
                          {t('Generate') || 'Generate'}
                        </Button>
                      </Stack>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} sm={5}>
              <FormControl fullWidth>
                <InputLabel>{t('CouponType') || 'Coupon Type'}</InputLabel>
                <Select value={form.type} onChange={handleChange('type')} label={t('CouponType') || 'Coupon Type'}>
                  <MenuItem value="discount">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LocalOfferIcon fontSize="small" color="warning" />
                      <span>{t('DiscountCoupon') || 'Discount Coupon'}</span>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="voucher">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CardGiftcardIcon fontSize="small" color="secondary" />
                      <span>{t('PurchaseVoucher') || 'Purchase Voucher'}</span>
                    </Stack>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label={t('Description') || 'Description'}
                value={form.description}
                onChange={handleChange('description')}
                placeholder={t('CouponDescPlaceholder') || 'e.g. Summer sale - 25% off all electronics'}
                helperText={t('DescriptionHelp') || 'Optional - helps you identify this coupon later'}
              />
            </Grid>
          </Grid>
        </SectionCard>

        {/* SECTION 2: DISCOUNT CONFIG */}
        <SectionCard
          icon={form.discountType === 'percentage' ? <PercentIcon fontSize="small" /> : <AttachMoneyIcon fontSize="small" />}
          title={t('DiscountConfig') || 'Discount Configuration'}
          subtitle={t('DiscountConfigDesc') || 'How the discount is calculated'}
          color="#ed6c02"
        >
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>{t('DiscountType') || 'Discount Type'}</InputLabel>
                <Select value={form.discountType} onChange={handleChange('discountType')} label={t('DiscountType') || 'Discount Type'}>
                  <MenuItem value="percentage">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PercentIcon fontSize="small" />
                      <span>{t('Percentage') || 'Percentage'}</span>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="fixedAmount">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AttachMoneyIcon fontSize="small" />
                      <span>{t('FixedAmount') || 'Fixed Amount'}</span>
                    </Stack>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label={form.discountType === 'percentage' ? (t('PercentageValue') || 'Percentage') : (t('DiscountAmount') || 'Amount')}
                value={form.value}
                onChange={handleChange('value')}
                error={!!errors.value}
                helperText={errors.value}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Chip label={form.discountType === 'percentage' ? '%' : form.currency} size="small" color="warning" variant="outlined" sx={{ fontWeight: 700 }} />
                    </InputAdornment>
                  )
                }}
                inputProps={{ min: 0, max: form.discountType === 'percentage' ? 100 : undefined, step: '0.01' }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>{t('Currency') || 'Currency'}</InputLabel>
                <Select value={form.currency} onChange={handleChange('currency')} label={t('Currency') || 'Currency'}>
                  {CURRENCIES.map(c => (
                    <MenuItem key={c.value} value={c.value}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={c.symbol} size="small" sx={{ fontWeight: 700, minWidth: 35 }} />
                        <span>{c.value}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label={t('MinOrderAmount') || 'Minimum Order Amount'}
                value={form.minOrderAmount}
                onChange={handleChange('minOrderAmount')}
                helperText={t('MinOrderAmountHelp') || 'Leave empty or 0 for no minimum'}
                InputProps={{ endAdornment: <InputAdornment position="end">{form.currency}</InputAdornment> }}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>

            {form.discountType === 'percentage' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('MaxDiscountAmount') || 'Maximum Discount Cap'}
                  value={form.maxDiscountAmount}
                  onChange={handleChange('maxDiscountAmount')}
                  helperText={t('MaxDiscountAmountHelp') || 'Leave empty for no cap'}
                  InputProps={{ endAdornment: <InputAdornment position="end">{form.currency}</InputAdornment> }}
                  inputProps={{ min: 0, step: '0.01' }}
                />
              </Grid>
            )}
          </Grid>
        </SectionCard>

        {/* SECTION 3: USAGE LIMITS + DATE RANGE */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <SectionCard
              icon={<LockIcon fontSize="small" />}
              title={t('UsageLimits') || 'Usage Limits'}
              subtitle={t('UsageLimitsDesc') || 'Control how many times it can be used'}
              color="#9c27b0"
            >
              <Stack spacing={2.5}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('TotalUsageLimit') || 'Total Usage Limit'}
                  value={form.usageLimit}
                  onChange={handleChange('usageLimit')}
                  helperText={t('TotalUsageLimitHelp') || 'Empty = unlimited'}
                  inputProps={{ min: 1 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Typography variant="caption" color="text.secondary">MAX</Typography></InputAdornment>
                  }}
                />
                <TextField
                  fullWidth
                  type="number"
                  label={t('PerUserLimit') || 'Uses Per Customer'}
                  value={form.usageLimitPerUser}
                  onChange={handleChange('usageLimitPerUser')}
                  helperText={t('PerUserLimitHelp') || 'Per customer limit'}
                  inputProps={{ min: 1 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" color="action" /></InputAdornment>
                  }}
                />
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard
              icon={<EventIcon fontSize="small" />}
              title={t('DateRange') || 'Validity Period'}
              subtitle={t('DateRangeDesc') || 'When can customers use this coupon'}
              color="#2e7d32"
            >
              <Stack spacing={2.5}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label={t('StartDate') || 'Start Date'}
                  value={form.startDate}
                  onChange={handleChange('startDate')}
                  helperText={t('StartDateHelp') || 'Empty = active immediately'}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  type="datetime-local"
                  label={t('EndDate') || 'End Date'}
                  value={form.endDate}
                  onChange={handleChange('endDate')}
                  error={!!errors.endDate}
                  helperText={errors.endDate || (t('EndDateHelp') || 'Empty = never expires')}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            </SectionCard>
          </Grid>
        </Grid>

        {/* SECTION 4: BRANCH ASSIGNMENT */}
        <SectionCard
          icon={<StoreIcon fontSize="small" />}
          title={t('BranchAssignment') || 'Branch Assignment'}
          subtitle={t('BranchAssignmentDesc') || 'Which branches accept this coupon'}
          color="#5e35b1"
        >
          <Alert
            severity={form.branchIds.length === 0 ? 'info' : 'success'}
            icon={form.branchIds.length === 0 ? <PublicIcon /> : <StoreIcon />}
            sx={{ mb: 2.5, borderRadius: 2 }}
          >
            {form.branchIds.length === 0
              ? (t('GlobalCouponInfo') || 'No branches selected — this coupon will be valid for ALL branches (global).')
              : `${form.branchIds.length} ${t('BranchesSelected') || 'branch(es) selected — coupon is branch-specific.'}`
            }
          </Alert>

          <Autocomplete
            multiple
            options={branches}
            value={selectedBranches}
            loading={branchesLoading}
            getOptionLabel={(option) => option.Name || `Branch #${option.Id}`}
            isOptionEqualToValue={(option, value) => option.Id === value.Id}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              const s = inputValue.toLowerCase();
              return options.filter(b =>
                (b.Name || '').toLowerCase().includes(s) ||
                (b.Address || '').toLowerCase().includes(s) ||
                (b.PhoneNumber || '').includes(s)
              );
            }}
            onChange={handleBranchesChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('SearchBranches') || 'Search branches by name, address or phone...'}
                placeholder={form.branchIds.length === 0 ? (t('AllBranchesPlaceholder') || 'Leave empty for all branches') : ''}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return (
                <li key={option.Id} {...rest}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', py: 0.5 }}>
                    <Avatar sx={{ bgcolor: alpha('#5e35b1', 0.15), color: '#5e35b1', width: 32, height: 32 }}>
                      <StoreIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{option.Name}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {[option.Address, option.PhoneNumber].filter(Boolean).join(' • ')}
                      </Typography>
                    </Box>
                  </Stack>
                </li>
              );
            }}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip key={option.Id} {...tagProps} icon={<StoreIcon />} label={option.Name} size="small" sx={{ borderRadius: 2 }} />
                );
              })
            }
            noOptionsText={t('NoBranchesFound') || 'No branches found'}
            loadingText={t('LoadingBranches') || 'Loading branches...'}
          />

          {form.branchIds.length > 0 && (
            <Button size="small" onClick={clearBranches} sx={{ mt: 1.5, textTransform: 'none' }} color="inherit">
              {t('ClearSelection') || 'Clear selection (make global)'}
            </Button>
          )}
        </SectionCard>

        {/* SECTION 5: USER ASSIGNMENT */}
        <SectionCard
          icon={<PeopleIcon fontSize="small" />}
          title={t('UserAssignment') || 'User Assignment'}
          subtitle={t('UserAssignmentDesc') || 'Restrict coupon to specific customers'}
          color="#e65100"
        >
                    <Alert
            severity={form.userIds.length === 0 ? 'info' : 'warning'}
            icon={form.userIds.length === 0 ? <PeopleIcon /> : <PersonIcon />}
            sx={{ mb: 2.5, borderRadius: 2 }}
          >
            {form.userIds.length === 0
              ? (t('AllUsersInfo') || 'No users selected — ALL users can use this coupon.')
              : `${form.userIds.length} ${t('UsersRestricted') || 'user(s) selected — only these customers can use this coupon.'}`
            }
          </Alert>

          <Autocomplete
            multiple
            options={users}
            value={selectedUsers}
            loading={usersLoading}
            getOptionLabel={getUserLabel}
            isOptionEqualToValue={(option, value) => option.Id === value.Id}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              const s = inputValue.toLowerCase();
              return options.filter(u =>
                getUserDisplayName(u).toLowerCase().includes(s) ||
                getUserPhone(u).includes(s) ||
                (u.Email || '').toLowerCase().includes(s) ||
                String(u.Id).includes(s)
              );
            }}
            onChange={handleUsersChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('SearchUsers') || 'Search users by name, phone or email...'}
                placeholder={form.userIds.length === 0 ? (t('AllUsersPlaceholder') || 'Leave empty to allow all users') : ''}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return (
                <li key={option.Id} {...rest}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', py: 0.5 }}>
                    <Avatar sx={{ bgcolor: alpha('#e65100', 0.15), color: '#e65100', width: 32, height: 32, fontSize: '0.8rem' }}>
                      {getUserInitial(option)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {getUserDisplayName(option) || `User #${option.Id}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {getUserSecondary(option)}
                      </Typography>
                    </Box>
                    {form.userIds.includes(option.Id) && (
                      <CheckCircleIcon fontSize="small" color="success" />
                    )}
                  </Stack>
                </li>
              );
            }}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={option.Id}
                    {...tagProps}
                    avatar={
                      <Avatar sx={{ bgcolor: alpha('#e65100', 0.2), color: '#e65100', width: 24, height: 24, fontSize: '0.7rem' }}>
                        {getUserInitial(option)}
                      </Avatar>
                    }
                    label={getUserChipLabel(option)}
                    size="small"
                    sx={{ borderRadius: 2 }}
                  />
                );
              })
            }
            noOptionsText={t('NoUsersFound') || 'No users found'}
            loadingText={t('LoadingUsers') || 'Loading users...'}
          />

          {form.userIds.length > 0 && (
            <Stack direction="row" spacing={1.5} mt={1.5} alignItems="center">
              <Button size="small" onClick={clearUsers} sx={{ textTransform: 'none' }} color="inherit">
                {t('ClearUserSelection') || 'Clear selection (allow all users)'}
              </Button>
              <Chip
                label={`${form.userIds.length} ${t('UsersSelected') || 'user(s) selected'}`}
                size="small"
                color="warning"
                variant="outlined"
                icon={<PeopleIcon />}
              />
            </Stack>
          )}
        </SectionCard>

        {/* SUBMIT BUTTONS */}
        <Card
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            bgcolor: alpha(theme.palette.primary.main, 0.02)
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
              {form.code && (
                <Chip label={form.code.toUpperCase()} size="small" sx={{ fontFamily: 'monospace', fontWeight: 700 }} color="primary" />
              )}
              {form.value && (
                <Chip
                  label={form.discountType === 'percentage' ? `${form.value}%` : `${form.value} ${form.currency}`}
                  size="small" color="warning" variant="outlined"
                />
              )}
              <Chip
                label={form.branchIds.length === 0 ? (t('AllBranches') || 'All Branches') : `${form.branchIds.length} ${t('Branches') || 'Branches'}`}
                size="small" icon={form.branchIds.length === 0 ? <PublicIcon /> : <StoreIcon />} variant="outlined"
              />
              <Chip
                label={form.userIds.length === 0 ? (t('AllUsers') || 'All Users') : `${form.userIds.length} ${t('Users') || 'Users'}`}
                size="small" icon={form.userIds.length === 0 ? <PeopleIcon /> : <PersonIcon />} variant="outlined"
              />
            </Stack>

            <Stack direction="row" spacing={1.5}>
              <Button variant="outlined" onClick={() => navigate('/admin/coupons')} sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}>
                {t('Cancel') || 'Cancel'}
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                disabled={saving}
                sx={{
                  textTransform: 'none', fontWeight: 600, px: 4, borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  '&:hover': { boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)' }
                }}
              >
                {saving
                  ? (t('Saving') || 'Saving...')
                  : isEdit
                    ? (t('UpdateCoupon') || 'Update Coupon')
                    : (t('CreateCoupon') || 'Create Coupon')
                }
              </Button>
            </Stack>
          </Stack>
        </Card>

      </form>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}